<script setup lang="ts">
import MmIcon from '../../base/MmIcon.vue'
import { safeBadgeIconUrl } from '../contracts.mjs'
const props = defineProps<{ icon: string; tier: number; single?: boolean; label: string; src?: string; retryable?: boolean }>()
const { t } = useI18n()
// The server owns badge artwork. Missing or failed configuration must stay visible.
const primary = computed(() => safeBadgeIconUrl(props.src))
const image = ref<HTMLImageElement | null>(null), failed = ref(false), loaded = ref(false), generation = ref(0)
const source = computed(() => failed.value ? '' : primary.value)
const imageError = computed(() => {
  const current = generation.value, src = source.value
  return () => { if (current === generation.value && src === source.value) failed.value = true }
})
const checkImage = () => { if (image.value?.complete) { if (image.value.naturalWidth === 0) imageError.value(); else loaded.value = true } }
function retry() { ++generation.value; failed.value = false; loaded.value = false; void nextTick(checkImage) }
watch(primary, retry, { flush: 'sync' })
watch(image, checkImage, { flush: 'post' })
onMounted(checkImage)
</script>
<template>
  <span v-if="source" class="creator-badge badge-image-wrap" :class="{ 'is-loading': !loaded }"><img :key="`${source}:${generation}`" ref="image" class="badge-remote" :class="{ locked: tier === 0 }" :src="source" :alt="label" width="72" height="72" loading="lazy" decoding="async" referrerpolicy="no-referrer" :onError="imageError" @load="loaded = true"></span>
  <span v-else class="creator-badge badge-missing" :title="t('incRewards.imageUnavailable')" data-badge-image-missing>
    <MmIcon name="image" :size="24" /><span class="badge-missing-label">{{ t('incRewards.imageUnavailable') }}</span>
    <button v-if="retryable && primary" type="button" @click.stop="retry">{{ t('common.retry') }}</button>
  </span>
</template>
<style scoped>
.creator-badge { display: block; flex-shrink: 0; width: 72px; height: 72px; object-fit: contain; }
.locked { filter: grayscale(1); opacity: .7; }
.badge-image-wrap { border-radius: var(--r-pill); overflow: hidden; background: var(--surface-2); }
.badge-image-wrap img { width: 100%; height: 100%; }
.badge-image-wrap.is-loading img { opacity: 0; }
.badge-image-wrap:not(.is-loading) { background: none; }
.badge-missing-label { font-size: var(--t-micro); line-height: 1.3; text-align: center; }
.badge-missing { height: auto; min-height: 72px; padding: var(--s-1); gap: var(--s-1); display: flex; flex-direction: column; align-items: center; justify-content: center; color: var(--text-3); background: var(--surface-2); border-radius: var(--r-pill); }
.badge-missing button { min-height: 44px; min-width: 44px; font-size: var(--t-small); color: var(--text-2); }
.badge-missing button:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }
</style>
